import {
  AppName,
  getDateString,
  getTimeString,
  MessageThread,
  PopulatedThreadMessage,
  ThreadMessage,
  ThreadMessageWithThreadData,
  ThreadTab,
  ThreadType,
  toSearchString,
  xor,
} from '@hb/shared'
import { ThreadsListThread } from '../types/ThreadsList'

export const sortThreadsByMostRecentMessage = (threads: ThreadsListThread[]) =>
  threads.sort((a, b) => b.thread.mostRecentMessageOn - a.thread.mostRecentMessageOn)

export const sortMessagesByMostRecent = <T extends ThreadMessage = ThreadMessage>(
  messages: T[],
): T[] => messages.sort((a, b) => a.createdOn - b.createdOn)

export const populateThreadMessage = <
  T extends ThreadMessageWithThreadData = ThreadMessageWithThreadData,
>(
  uid: string | null,
  message: T,
  previousMessage: T | undefined,
  nextMessage: T | undefined,
): PopulatedThreadMessage<T> => {
  const date = new Date(message.createdOn)
  const timeString = getTimeString(date)
  const dateString = getDateString(date, 'short', false)
  const prevTimeString = previousMessage ? getTimeString(new Date(previousMessage.createdOn)) : ''
  const nextTimeString = nextMessage ? getTimeString(new Date(nextMessage.createdOn)) : ''
  const prevDateString = previousMessage
    ? getDateString(new Date(previousMessage.createdOn), 'short', false)
    : ''
  const nextDateString = nextMessage
    ? getDateString(new Date(nextMessage.createdOn), 'short', false)
    : ''
  const sentBySameAsPrevious =
    previousMessage?.createdByGroup === message.createdByGroup &&
    previousMessage?.createdBy === message.createdBy
  const sentBySameAsNext =
    previousMessage?.createdByGroup === message.createdByGroup &&
    nextMessage?.createdBy === message.createdBy

  const sentByMe = !!(uid && message.createdBy === uid)
  const sentAtSameDateAsNext = dateString === nextDateString
  const sentAtSameDateAsPrevious = dateString === prevDateString
  const sentAtSameTimeAsNext = sentAtSameDateAsNext && timeString === nextTimeString
  const sentAtSameTimeAsPrevious = sentAtSameDateAsPrevious && timeString === prevTimeString

  return {
    ...message,
    timeString,
    dateString,
    sentBySameAsPrevious,
    sentByMe,
    sentBySameAsNext,
    sentAtSameTimeAsPrevious,
    sentAtSameTimeAsNext,
    sentAtSameDateAsPrevious,
    sentAtSameDateAsNext,
  }
}

export const populateThreadMessages = <
  T extends ThreadMessageWithThreadData = ThreadMessageWithThreadData,
>(
  uid: string | null,
  messages: Array<T>,
): Array<PopulatedThreadMessage<T>> =>
  sortMessagesByMostRecent(messages).map((message, i, arr) => {
    const previousMessage = i > 0 ? arr[i - 1] : undefined
    const nextMessage = i < arr.length - 1 ? arr[i + 1] : undefined
    return populateThreadMessage(uid, message, previousMessage, nextMessage)
  })

export const filterThreads = (threads: ThreadsListThread[], selectedTab?: ThreadTab | null) => {
  if (!selectedTab) return []
  switch (selectedTab) {
    case 'patient':
      return threads.filter(t => t.thread.accessLevel === 'patient')
    case 'practice':
      return threads.filter(t => t.thread.accessLevel === 'practice')
    case 'admin':
      return threads.filter(t => t.thread.accessLevel === 'admin')
    default:
      return []
  }
}

export const fillEmptyThread = (
  uid: string | null,
  threadType: ThreadType,
  threadId: string,
  threadTitle: string,
  thread: MessageThread | null,
): ThreadsListThread => {
  if (thread) {
    return {
      thread: { ...thread, title: threadTitle, type: threadType },
      threadId,
      isUnread: !!uid && thread.unreadBy.includes(uid),
      threadType,
    }
  }
  return {
    thread: {
      type: threadType,
      title: threadTitle,
      searchString: toSearchString(threadTitle),
      accessLevel: 'patient',
      subscribers: [],
      mostRecentMessages: [],
      mostRecentMessageOn: 0,
      mostRecentMessageBy: null,
      unreadBy: [],
      readBy: [],
    },
    isUnread: false,
    threadId,
    threadType,
  }
}

export const getRightJustified = (appName: AppName, message: PopulatedThreadMessage) => {
  switch (message?.threadType) {
    case ThreadType.ASSESSMENT:
      return xor(message.createdByGroup === 'admin', appName === 'providers-app')
    default:
      return message.sentByMe
  }
}
